import { Statistics, Publication, ChartData, AnalysisMeta } from "@/types/types";

interface ProcessedPublicationData {
  statistics: Statistics;
  chartData: ChartData;
  authors: Set<string>;
  types: Set<string>;
  journals: Set<string>;
  publications: Publication[];
  meta: AnalysisMeta;
}

interface ImpactFactorData {
  year: number;
  journal: string;
  eissn: string;
  pissn: string;
  if: number;
  categoryMedianIf: number;
  quartile: string;
}

interface NorwayData {
  printIssn: string;
  onlineIssn: string;
  levels: Record<number, number | null>;
}

export const analyze = async (
  author: {
    mtid: string;
    name: string;
    degrees: string[];
  },
  rawPublications: Publication[],
  translations?: Record<string, string>,
  excludeNorwayLevel0: boolean = false
): Promise<ProcessedPublicationData> => {
  if (!rawPublications?.length) {
    return {
      statistics: generateEmptyStatistics(),
      chartData: generateEmptyChartData(),
      authors: new Set<string>(),
      types: new Set<string>(),
      journals: new Set<string>(),
      publications: [],
      meta: {
        lastUpdated: new Date().toLocaleString(),
        ifListVersionYear: maxIFYear,
        norwayListVersionYear: norwayMaxYear,
        excludeNorwayLevel0,
      },
    };
  }

  const publications = rawPublications.map((data) => new Publication(data));
  const stats = generateEmptyStatistics();
  const yearlyData: Record<
    string,
    {
      publications: number;
      iscore: number;
      qscore: number;
      publicationsWithIF: number;
      rankings: Record<string, number>;
    }
  > = {};
  const authors = new Set<string>();
  const types = new Set<string>();
  const journals = new Set<string>();
  const citations: number[] = [];

  for (const pub of publications) {
    if (!pub.isScientific()) continue;

    if (
      !pub.authorships?.some(
        (a) => a.author?.mtid === Number(author.mtid) && a.authorTyped === true
      )
    ) {
      pub.contributorOnly = true;
      continue;
    }

    loadNorwayLevel(pub);

    // Skip calculations if excludeNorwayLevel0 is enabled and publication has level 0
    const shouldExclude = excludeNorwayLevel0 && pub.norwayLevel === 0;

    if (!shouldExclude) {
      stats.totalScientificPublications++;
      const impactFactor = await getImpactFactor(pub);
      const year = pub.publishedYear?.toString() || "Unknown";
      const authorShare = pub.getAuthorShare(author.mtid);

      // Calculate Q score
      const qScore = await pub.getQScore(authorShare, impactFactor);
      if (qScore.source === "articles")
        stats.qScore.fromArticles += qScore.qscore;
      else stats.qScore.fromBooks += qScore.qscore;

      if (impactFactor > 0 && qScore.qscore > 0) {
        stats.impactFactor.total += impactFactor;
        stats.impactFactor.relative += impactFactor * authorShare;
        pub.IFindex = impactFactor;
        stats.publicationsWithIF++;
        if (pub.authorships?.length === 1) stats.singleAuthorPublicationsWithIF++;
      }

      // Calculate I score
      const iScoreData = pub.getIScore();
      stats.totalScientificCitations += iScoreData.scientificCitations;
      stats.iScore += iScoreData.iscore;
      stats.iScoreWOS += iScoreData.iscoreWos;
      if (iScoreData.scientificCitations > 0)
        citations.push(iScoreData.scientificCitations);

      // Update publication type stats
      update_PublicationsByType_Stats(pub, stats);

      // Yearly data update
      yearlyData[year] = yearlyData[year] || {
        publications: 0,
        iscore: 0,
        qscore: 0,
        publicationsWithIF: 0,
        rankings: { D1: 0, Q1: 0, Q2: 0, Q3: 0, Q4: 0, "N/A": 0 },
      };
      yearlyData[year].publications++;
      yearlyData[year].iscore += pub.iscore || 0;
      yearlyData[year].qscore += pub.qscore || 0;
      if (impactFactor > 0 && qScore.qscore > 0) {
        yearlyData[year].publicationsWithIF++;
      }
      yearlyData[year].rankings[
        pub.getRating() as keyof (typeof yearlyData)[typeof year]["rankings"]
      ]++;
    }

    // Types gathering (always, even if excluded)
    types.add(pub.getPublicationType(translations));

    // Authors gathering (always, even if excluded)
    pub.authorships?.forEach((a) =>
      authors.add(a.author?.label || `${a.familyName} ${a.givenName}`)
    );

    // Journals gathering (always, even if excluded)
    if (pub.journal?.label) journals.add(pub.journal.label);
  }

  stats.qScore.total = stats.qScore.fromArticles + stats.qScore.fromBooks;
  stats.hIndex = calculateHIndex(citations);

  return {
    statistics: stats,
    chartData: prepareChartDataFromYearlyData(yearlyData),
    authors: new Set(Array.from(authors).sort()),
    types,
    journals: new Set(Array.from(journals).sort()),
    publications,
    meta: {
      lastUpdated: new Date().toLocaleString("hu-HU"),
      ifListVersionYear: maxIFYear,
      norwayListVersionYear: norwayMaxYear,
      excludeNorwayLevel0,
    },
  };
};

const update_PublicationsByType_Stats = (
  pub: Publication,
  stats: Statistics
) => {
  const type = pub.type?.otypeName?.toLowerCase() || "";
  const category = pub.foreignEdition
    ? "foreign"
    : pub.foreignLanguage
    ? "hungarianForeign"
    : "hungarianNative";

  const typeMap: Record<string, keyof Statistics["publicationsByType"]> = {
    journalarticle: "scientificArticle",
    bookchapter: pub.subType?.name?.toLowerCase().includes("konferencia")
      ? "conferenceArticle"
      : "bookChapter",
    book: "book",
    patent: "patent",
    thesis: "thesis",
  };

  const publicationType = typeMap[type] || "other";
  stats.publicationsByType[publicationType][category]++;
};

const calculateHIndex = (citations: number[]): number => {
  if (!citations.length) return 0;
  citations.sort((a, b) => b - a);
  return citations.findIndex((c, i) => c <= i) || citations.length;
};

const prepareChartDataFromYearlyData = (yearlyData: Record<string, any>) => {
  const years = Object.keys(yearlyData).sort();
  const cumulative = {
    D1: 0,
    Q1: 0,
    Q2: 0,
    Q3: 0,
    Q4: 0,
    NA: 0,
    iScore: 0,
    qScore: 0,
    publicationsWithIF: 0,
  };

  return {
    publicationsWithRankings: {
      years,
      rankings: {
        D1: years.map((year) => yearlyData[year].rankings.D1),
        Q1: years.map((year) => yearlyData[year].rankings.Q1),
        Q2: years.map((year) => yearlyData[year].rankings.Q2),
        Q3: years.map((year) => yearlyData[year].rankings.Q3),
        Q4: years.map((year) => yearlyData[year].rankings.Q4),
        "N/A": years.map((year) => yearlyData[year].rankings["N/A"]),
      },
    },

    cumulativePublications: {
      years,
      rankings: {
        D1: years.map((year) => (cumulative.D1 += yearlyData[year].rankings.D1)),
        Q1: years.map((year) => (cumulative.Q1 += yearlyData[year].rankings.Q1)),
        Q2: years.map((year) => (cumulative.Q2 += yearlyData[year].rankings.Q2)),
        Q3: years.map((year) => (cumulative.Q3 += yearlyData[year].rankings.Q3)),
        Q4: years.map((year) => (cumulative.Q4 += yearlyData[year].rankings.Q4)),
        "N/A": years.map((year) => (cumulative.NA += yearlyData[year].rankings["N/A"])),
      },
    },

    iScore: {
      label: "Éves független hivatkozások",
      years,
      data: years.map((year) => yearlyData[year].iscore),
    },

    cumulativeIScore: {
      label: "Kumulatív független hivatkozások",
      years,
      data: years.map((year) => (cumulative.iScore += yearlyData[year].iscore)),
    },

    qScore: {
      label: "Éves Q pontszám",
      years,
      data: years.map((year) => yearlyData[year].qscore),
    },

    cumulativeQScore: {
      label: "Kumulatív Q pontszám",
      years,
      data: years.map((year) => (cumulative.qScore += yearlyData[year].qscore)),
    },

    impactFactor: {
      label: "Éves IF-os publikációk",
      years,
      data: years.map((year) => yearlyData[year].publicationsWithIF || 0),
    },

    cumulativeImpactFactor: {
      label: "Kumulatív IF-os publikációk",
      years,
      data: years.map(
        (year) =>
          (cumulative.publicationsWithIF +=
            yearlyData[year].publicationsWithIF || 0)
      ),
    },
  };
};

let IFData: ImpactFactorData[] | null = null;
let norwayData: NorwayData[] | null = null;

let maxIFYear: number | null = null;
let norwayMaxYear: number | null = null;

export const loadImpactFactorData = async (): Promise<ImpactFactorData[]> => {
  if (IFData) return IFData;

  const response = await fetch(`/iflist.csv`);
  if (!response.ok) {
    throw new Error(`Failed to load impact factor data: ${response.statusText}`);
  }

  IFData = (await response.text())
    .split("\n")
    .slice(1)
    .filter((line) => line.trim())
    .map((row) => {
      const [pissn, journal, if_, quartile, year, categoryMedianIf, , eissn] =
        row.split(",");
      const yearNum = Number(year);
      if (maxIFYear === null || yearNum > maxIFYear) {
        maxIFYear = yearNum;
      }
      return {
        year: yearNum,
        journal: journal.trim(),
        eissn: eissn.trim(),
        pissn: pissn.trim(),
        if: parseFloat(if_),
        categoryMedianIf: parseFloat(categoryMedianIf),
        quartile: quartile.trim(),
      };
    });
  return IFData;
};

export const loadNorwayData = async (): Promise<NorwayData[]> => {
  if (norwayData) return norwayData;

  const response = await fetch(`/norwaylist.csv`);
  if (!response.ok) {
    throw new Error(`Failed to load Norway data: ${response.statusText}`);
  }

  const lines = (await response.text()).split("\n");
  const yearColumns = lines[0]
    .split(",")
    .map((header, index) => {
      const match = header.match(/Level (\d{4})/);
      return match ? { year: parseInt(match[1]), index } : null;
    })
    .filter((x): x is { year: number; index: number } => x !== null);

  if (yearColumns.length > 0) {
    norwayMaxYear = yearColumns.reduce((m, c) => (c.year > m ? c.year : m), yearColumns[0].year);
  }

  norwayData = lines
    .slice(1)
    .filter((line) => line.trim())
    .map((row) => {
      const columns = row.split(",");
      const levels: Record<number, number | null> = {};
      yearColumns.forEach(({ year, index }) => {
        const value = columns[index].trim();
        levels[year] = value ? parseInt(value) : null;
      });
      return {
        printIssn: columns[0].trim(),
        onlineIssn: columns[1].trim(),
        levels,
      };
    });

  return norwayData;
};

const getImpactFactor = async (pub: Publication): Promise<number> => {
  if (!pub.journal) return 0;

  if (!IFData) await loadImpactFactorData();
  if (!IFData || !maxIFYear) return 0;

  const targetYear = Math.min(pub.publishedYear, maxIFYear);
  
  // Try to find exact year match
  const exactMatch = IFData.find(
    (ifct) =>
      ifct.year === targetYear &&
      (ifct.eissn === pub.journal?.eIssn ||
        ifct.pissn === pub.journal?.pIssn ||
        ifct.eissn === pub.journal?.pIssn ||
        ifct.pissn === pub.journal?.eIssn ||
        (pub.journal?.label &&
          ifct.journal.toUpperCase() === pub.journal.label.toUpperCase()) ||
        (pub.book?.title &&
          ifct.journal.toUpperCase() === pub.book.title.toUpperCase()))
  );

  return exactMatch?.if ?? 0;
};

const loadNorwayLevel = async (pub: Publication) => {
  if (!norwayData) await loadNorwayData();
  if (norwayData) {
    const entry = norwayData.find(
      (entry) =>
        entry.printIssn === pub.journal?.pIssn ||
        entry.printIssn === pub.journal?.eIssn ||
        entry.onlineIssn === pub.journal?.pIssn ||
        entry.onlineIssn === pub.journal?.eIssn
    );
    if (entry) pub.norwayLevel = entry.levels[pub.publishedYear] ?? undefined;
  }
};

const generateEmptyStatistics = (): Statistics => ({
  totalScientificPublications: 0,
  totalScientificCitations: 0,
  hIndex: 0,
  qScore: { total: 0, fromArticles: 0, fromBooks: 0 },
  iScore: 0,
  iScoreWOS: 0,
  impactFactor: { total: 0, relative: 0 },
  publicationsWithIF: 0,
  singleAuthorPublicationsWithIF: 0,
  publicationsByType: {
    scientificArticle: { foreign: 0, hungarianForeign: 0, hungarianNative: 0 },
    conferenceArticle: { foreign: 0, hungarianForeign: 0, hungarianNative: 0 },
    book: { foreign: 0, hungarianForeign: 0, hungarianNative: 0 },
    bookChapter: { foreign: 0, hungarianForeign: 0, hungarianNative: 0 },
    patent: { foreign: 0, hungarianForeign: 0, hungarianNative: 0 },
    thesis: { foreign: 0, hungarianForeign: 0, hungarianNative: 0 },
    other: { foreign: 0, hungarianForeign: 0, hungarianNative: 0 },
  },
});

const generateEmptyChartData = (): ChartData => ({
  publicationsWithRankings: {
    years: [],
    rankings: { D1: [], Q1: [], Q2: [], Q3: [], Q4: [], "N/A": [] },
  },
  cumulativePublications: {
    years: [],
    rankings: { D1: [], Q1: [], Q2: [], Q3: [], Q4: [], "N/A": [] },
  },
  iScore: { label: "", years: [], data: [] },
  cumulativeIScore: { label: "", years: [], data: [] },
  qScore: { label: "", years: [], data: [] },
  cumulativeQScore: { label: "", years: [], data: [] },
  impactFactor: { label: "", years: [], data: [] },
  cumulativeImpactFactor: { label: "", years: [], data: [] },
});
